<script lang="ts">
	import type { HTMLAnchorAttributes, HTMLButtonAttributes } from 'svelte/elements';
	
	type ButtonProps = {
		variant?: 'primary' | 'secondary' | 'ghost';
		size?: 'sm' | 'md' | 'lg';
		href?: string;
		disabled?: boolean;
		loading?: boolean;
		class?: string;
	};
	
	type $$Props = ButtonProps & 
		(ButtonProps['href'] extends string 
			? HTMLAnchorAttributes 
			: HTMLButtonAttributes);
	
	let {
		variant = 'primary',
		size = 'md',
		href = undefined,
		disabled = false,
		loading = false,
		class: className = '',
		onclick,
		children,
		...restProps
	}: ButtonProps & { children?: any; onclick?: (event: MouseEvent) => void } = $props();
	
	const tag = $derived(href ? 'a' : 'button');
	const isDisabled = $derived(disabled || loading);
	const classes = $derived([
		'btn',
		`btn--${variant}`,
		`btn--${size}`,
		loading && 'btn--loading',
		className
	].filter(Boolean).join(' '));
</script>

<svelte:element
	this={tag}
	class={classes}
	{href}
	disabled={tag === 'button' ? isDisabled : undefined}
	aria-disabled={isDisabled}
	aria-busy={loading}
	{onclick}
	{...restProps}
>
	{#if loading}
		<span class="btn__loader" aria-hidden="true">
			<svg class="spinner" viewBox="0 0 24 24" fill="none">
				<circle class="spinner__track" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
				<circle class="spinner__fill" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
			</svg>
		</span>
	{/if}
	<span class="btn__content" class:btn__content--loading={loading}>
		{@render children?.()}
	</span>
</svelte:element>

<style>
	.btn {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-2);
		font-family: var(--font-sans);
		font-weight: var(--font-weight-medium);
		border-radius: var(--radius-md);
		transition: var(--transition-colors), var(--transition-transform), var(--transition-shadow);
		cursor: pointer;
		border: 1px solid transparent;
		text-decoration: none;
		white-space: nowrap;
		user-select: none;
	}
	
	/* Sizes - More compact like Linear */
	.btn--sm {
		padding: var(--space-1_5) var(--space-2_5);
		font-size: var(--font-size-sm);
		min-height: 28px;
	}
	
	.btn--md {
		padding: var(--space-2) var(--space-3);
		font-size: var(--font-size-base);
		min-height: 34px;
	}
	
	.btn--lg {
		padding: var(--space-2_5) var(--space-4);
		font-size: var(--font-size-base);
		min-height: 40px;
	}

	/* Mobile: meet the ~48px tap-target guideline (Google mobile-friendly / Lighthouse). */
	@media (max-width: 768px) {
		.btn--sm,
		.btn--md,
		.btn--lg {
			min-height: 48px;
		}
	}
	
	/* Primary variant - Modern gradient style */
	.btn--primary {
		background: var(--gradient-brand);
		color: var(--button-primary-text);
		border: 1px solid hsl(var(--brand-h) var(--brand-s) calc(var(--brand-l) - 10%) / 0.2);
		box-shadow: var(--shadow-sm), inset 0 1px 0 0 hsl(0 0% 100% / 0.1);
		position: relative;
		overflow: hidden;
	}
	
	.btn--primary::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(135deg, hsl(0 0% 100% / 0.15) 0%, transparent 60%);
		opacity: 0;
		transition: opacity 0.3s ease;
	}
	
	.btn--primary:hover:not([aria-disabled="true"]) {
		transform: translateY(-1px);
		box-shadow: var(--shadow-md), inset 0 1px 0 0 hsl(0 0% 100% / 0.15);
		border-color: hsl(var(--brand-h) var(--brand-s) calc(var(--brand-l) - 15%) / 0.3);
	}
	
	.btn--primary:hover:not([aria-disabled="true"])::before {
		opacity: 1;
	}
	
	.btn--primary:active:not([aria-disabled="true"]) {
		transform: translateY(0);
		box-shadow: var(--shadow-sm), inset 0 2px 4px 0 hsl(0 0% 0% / 0.1);
	}
	
	/* Secondary variant */
	.btn--secondary {
		background: var(--button-secondary-bg);
		color: var(--button-secondary-text);
		border-color: var(--border-light);
	}
	
	.btn--secondary:hover:not([aria-disabled="true"]) {
		background: var(--button-secondary-hover);
		border-color: var(--border-default);
		transform: translateY(-1px);
	}
	
	.btn--secondary:active:not([aria-disabled="true"]) {
		transform: translateY(0);
	}
	
	/* Ghost variant */
	.btn--ghost {
		background: transparent;
		color: var(--text-primary);
		border-color: transparent;
	}
	
	.btn--ghost:hover:not([aria-disabled="true"]) {
		background: var(--hover-overlay);
		border-color: var(--border-light);
	}
	
	.btn--ghost:active:not([aria-disabled="true"]) {
		background: var(--active-overlay);
	}
	
	/* Disabled state */
	.btn[aria-disabled="true"] {
		opacity: 0.5;
		cursor: not-allowed;
		transform: none !important;
		box-shadow: none !important;
	}
	
	/* Loading state */
	.btn--loading {
		cursor: wait;
	}
	
	.btn__loader {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	
	.btn__content {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		transition: opacity var(--duration-fast);
	}
	
	.btn__content--loading {
		opacity: 0;
	}
	
	/* Spinner animation */
	.spinner {
		width: 1.25rem;
		height: 1.25rem;
		animation: rotate 1s linear infinite;
	}
	
	.spinner__track {
		opacity: 0.25;
	}
	
	.spinner__fill {
		stroke-dasharray: 63;
		stroke-dashoffset: 63;
		animation: fill 1.5s ease-in-out infinite;
		stroke-linecap: round;
		transform-origin: center;
	}
	
	@keyframes rotate {
		to {
			transform: rotate(360deg);
		}
	}
	
	@keyframes fill {
		0%, 25% {
			stroke-dashoffset: 63;
			transform: rotate(0);
		}
		50%, 75% {
			stroke-dashoffset: 16;
			transform: rotate(45deg);
		}
		100% {
			stroke-dashoffset: 63;
			transform: rotate(360deg);
		}
	}
</style>